import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ChartBarIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  SparklesIcon,
  UserIcon,
  BuildingOfficeIcon,
  RocketLaunchIcon
} from '@heroicons/react/24/outline'
import { API_BASE_URL } from '@/services/api'

interface OnboardingStatus {
  user_id: number
  tenant_id: number
  onboarding_completed: boolean
  onboarding_step: number
  tenant_name: string
  tenant_status: string
  trial_ends_at: string | null
}

export default function OnboardingPage() {
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState(1)
  const [loading, setLoading] = useState(false)
  const [status, setStatus] = useState<OnboardingStatus | null>(null)

  useEffect(() => {
    // Load onboarding status
    const token = localStorage.getItem('token')
    if (!token) {
      navigate('/login')
      return
    }

    fetch(`${API_BASE_URL}/onboarding/status`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
      .then(res => res.json())
      .then(data => {
        setStatus(data)
        setCurrentStep(data.onboarding_step + 1 || 1)
      })
      .catch(err => {
        console.error('Error loading status:', err)
      })
  }, [navigate])

  const updateStep = async (step: number, completed: boolean = false) => {
    const token = localStorage.getItem('token')

    try {
      await fetch(`${API_BASE_URL}/onboarding/update-step`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ step, completed })
      })
    } catch (err) {
      console.error('Error updating step:', err)
    }
  }

  const handleNext = async () => {
    await updateStep(currentStep)

    if (currentStep < 3) {
      setCurrentStep(currentStep + 1)
    } else {
      // Complete onboarding
      setLoading(true)
      const token = localStorage.getItem('token')

      try {
        await fetch(`${API_BASE_URL}/onboarding/complete`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        // Navigate to dashboard using React Router (no page reload)
        navigate('/dashboard')
      } catch (err) {
        console.error('Error completing onboarding:', err)
        setLoading(false)
      }
    }
  }

  const handleSkip = async () => {
    setLoading(true)
    const token = localStorage.getItem('token')

    try {
      await fetch(`${API_BASE_URL}/onboarding/skip`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      navigate('/dashboard')
    } catch (err) {
      console.error('Error skipping onboarding:', err)
    } finally {
      setLoading(false)
    }
  }

  const steps = [
    {
      number: 1,
      title: 'Bem-vindo ao FinPro!',
      icon: SparklesIcon,
      color: 'blue',
      content: (
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full mb-6">
            <SparklesIcon className="h-10 w-10 text-blue-600" />
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Bem-vindo, {status?.tenant_name || 'Empresa'}!
          </h2>

          <p className="text-lg text-gray-600 mb-6 max-w-xl mx-auto">
            Estamos felizes em ter você conosco! Vamos configurar sua conta em 3 passos simples
            para você aproveitar ao máximo todas as funcionalidades do FinPro.
          </p>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 max-w-md mx-auto">
            <div className="flex items-start">
              <CheckCircleIcon className="h-6 w-6 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
              <div className="text-left">
                <h3 className="font-semibold text-gray-900 mb-2">
                  Seu período de teste já começou!
                </h3>
                <p className="text-sm text-gray-600">
                  Você tem{' '}
                  <span className="font-semibold text-blue-600">
                    {status?.trial_ends_at &&
                      Math.ceil((new Date(status.trial_ends_at).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
                    } dias
                  </span>
                  {' '}de teste grátis para explorar todos os recursos premium sem compromisso.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-3 gap-4 text-center max-w-2xl mx-auto">
            <div>
              <ChartBarIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600">Dashboard Completo</p>
            </div>
            <div>
              <BuildingOfficeIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600">Multi-empresa</p>
            </div>
            <div>
              <RocketLaunchIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600">Sem limites</p>
            </div>
          </div>
        </div>
      )
    },
    {
      number: 2,
      title: 'Configure sua Empresa',
      icon: BuildingOfficeIcon,
      color: 'purple',
      content: (
        <div>
          <div className="inline-flex items-center justify-center w-20 h-20 bg-purple-100 rounded-full mb-6">
            <BuildingOfficeIcon className="h-10 w-10 text-purple-600" />
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Personalize sua Experiência
          </h2>

          <p className="text-lg text-gray-600 mb-8 max-w-xl mx-auto">
            Configure as informações da sua empresa para aproveitar recursos como:
          </p>

          <div className="grid md:grid-cols-2 gap-4 max-w-3xl mx-auto text-left">
            {[
              {
                title: 'Relatórios Personalizados',
                description: 'DRE, Balanço e Fluxo de Caixa com o logo da sua empresa'
              },
              {
                title: 'Múltiplos Usuários',
                description: 'Convide sua equipe e defina permissões de acesso'
              },
              {
                title: 'Integrações Bancárias',
                description: 'Conecte suas contas para sincronização automática'
              },
              {
                title: 'Categorias Customizadas',
                description: 'Crie categorias específicas para o seu negócio'
              }
            ].map((feature, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-start">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 bg-yellow-50 border border-yellow-200 rounded-lg p-4 max-w-md mx-auto">
            <p className="text-sm text-gray-700">
              💡 <strong>Dica:</strong> Você pode configurar tudo isso depois também.
              Acesse <span className="font-semibold">Configurações → Empresa</span> no menu.
            </p>
          </div>
        </div>
      )
    },
    {
      number: 3,
      title: 'Pronto para Começar!',
      icon: RocketLaunchIcon,
      color: 'green',
      content: (
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
            <RocketLaunchIcon className="h-10 w-10 text-green-600" />
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Tudo Pronto! 🎉
          </h2>

          <p className="text-lg text-gray-600 mb-8 max-w-xl mx-auto">
            Você está pronto para começar a usar o FinPro! Veja o que você pode fazer agora:
          </p>

          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {[
              {
                title: 'Adicionar Transações',
                description: 'Registre receitas e despesas manualmente ou importe extratos',
                action: 'Ir para Transações'
              },
              {
                title: 'Conectar Bancos',
                description: 'Sincronize suas contas bancárias automaticamente',
                action: 'Ver Integrações'
              },
              {
                title: 'Ver Dashboard',
                description: 'Visualize um resumo completo das suas finanças',
                action: 'Abrir Dashboard'
              }
            ].map((option, index) => (
              <div key={index} className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-green-500 transition">
                <h3 className="font-bold text-gray-900 mb-2">
                  {option.title}
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  {option.description}
                </p>
                <button className="text-sm text-green-600 hover:text-green-700 font-semibold">
                  {option.action} →
                </button>
              </div>
            ))}
          </div>

          <div className="mt-8 bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-6 max-w-2xl mx-auto">
            <div className="flex items-start">
              <SparklesIcon className="h-6 w-6 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
              <div className="text-left">
                <h3 className="font-semibold text-gray-900 mb-2">
                  Precisa de Ajuda?
                </h3>
                <p className="text-sm text-gray-600">
                  Nossa central de ajuda está disponível 24/7 com tutoriais em vídeo,
                  guias passo a passo e suporte via chat. Clique no ícone de ajuda (?) no canto superior direito.
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 via-white to-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <ChartBarIcon className="h-8 w-8 text-blue-600" />
              <span className="text-2xl font-bold text-gray-900">FinPro</span>
            </div>

            <button
              onClick={handleSkip}
              disabled={loading}
              className="text-gray-600 hover:text-gray-900 text-sm font-medium"
            >
              Pular Tutorial
            </button>
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-center space-x-4 max-w-2xl mx-auto">
            {steps.map((step, index) => (
              <React.Fragment key={step.number}>
                <div className="flex flex-col items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition ${
                      currentStep === step.number
                        ? 'bg-blue-600 text-white'
                        : currentStep > step.number
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {currentStep > step.number ? (
                      <CheckCircleIcon className="h-6 w-6" />
                    ) : (
                      step.number
                    )}
                  </div>
                  <span className="text-xs text-gray-600 mt-2 hidden sm:block">
                    Passo {step.number}
                  </span>
                </div>

                {index < steps.length - 1 && (
                  <div
                    className={`flex-1 h-1 rounded transition ${
                      currentStep > step.number ? 'bg-green-500' : 'bg-gray-200'
                    }`}
                    style={{ maxWidth: '100px' }}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-8 md:p-12">
            {steps[currentStep - 1]?.content}
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center mt-8">
            <button
              onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
              disabled={currentStep === 1}
              className="px-6 py-3 text-gray-700 hover:text-gray-900 disabled:text-gray-400 disabled:cursor-not-allowed font-medium"
            >
              ← Voltar
            </button>

            <div className="text-sm text-gray-500">
              {currentStep} de {steps.length}
            </div>

            <button
              onClick={handleNext}
              disabled={loading}
              className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-semibold flex items-center space-x-2 transition"
            >
              {loading ? (
                <>
                  <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                  <span>Carregando...</span>
                </>
              ) : currentStep === 3 ? (
                <>
                  <span>Ir para o Dashboard</span>
                  <RocketLaunchIcon className="h-5 w-5" />
                </>
              ) : (
                <>
                  <span>Próximo</span>
                  <ArrowRightIcon className="h-5 w-5" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
